/**
 * Dialog for creating new payment requests
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { QrCode, DollarSign, Loader2 } from 'lucide-react';
import { EXPIRY_OPTIONS } from './constants';

const CreateRequestDialog = ({
  open,
  onOpenChange,
  fixedAmount,
  setFixedAmount,
  amount,
  setAmount,
  currency,
  setCurrency,
  note,
  setNote,
  expiresIn,
  setExpiresIn,
  currencies,
  onCreate,
  creating
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Payment Request</DialogTitle>
          <DialogDescription>Generate a QR code or payment link</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="flex items-center justify-between p-3 rounded-xl bg-muted">
            <div>
              <p className="font-medium">Fixed Amount</p>
              <p className="text-sm text-muted-foreground">
                {fixedAmount ? 'Payer must send exact amount' : 'Payer chooses amount'}
              </p>
            </div>
            <Switch
              checked={fixedAmount}
              onCheckedChange={setFixedAmount}
              data-testid="fixed-amount-toggle"
            />
          </div>

          {fixedAmount && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-9 rounded-xl"
                    min="0.01"
                    step="0.01"
                    data-testid="request-amount-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="rounded-xl" data-testid="request-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.slice(0, 10).map((curr) => (
                      <SelectItem key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Note (Optional)</Label>
            <Textarea
              placeholder="What's this for?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-xl resize-none"
              rows={2}
              data-testid="request-note-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Expires In</Label>
            <Select value={expiresIn} onValueChange={setExpiresIn}>
              <SelectTrigger className="rounded-xl" data-testid="expiry-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">
            Cancel
          </Button>
          <Button onClick={onCreate} disabled={creating} className="rounded-xl" data-testid="create-request-submit">
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <QrCode className="w-4 h-4 mr-2" />
                Create Request
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRequestDialog;
